import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Clock, 
  User, 
  Calendar,
  Download,
  Save,
  AlertTriangle,
  Target,
  Users,
  Shield,
  Activity,
  X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { planoTrabalhoService, PlanoTrabalhoCompleto, AcaoDB, MetaDB, ObjetivoDB } from '../services/planoTrabalhoService';

// Usar as interfaces do serviço
type AcaoPlanoTrabalho = AcaoDB;
type PlanoTrabalhoCIPA = PlanoTrabalhoCompleto;

const PlanoTrabalhoTab: React.FC = () => {
  const { user, empresaSelecionada } = useAuth();
  const [planoTrabalho, setPlanoTrabalho] = useState<PlanoTrabalhoCIPA | null>(null);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(false);
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
  const [mostrarModalAcao, setMostrarModalAcao] = useState(false);
  const [novaMeta, setNovaMeta] = useState('');
  const [mostrarInputMeta, setMostrarInputMeta] = useState(false);
  const [acaoEditando, setAcaoEditando] = useState<AcaoPlanoTrabalho | null>(null);
  const [novaAcao, setNovaAcao] = useState<Partial<AcaoPlanoTrabalho>>({
    descricao: '',
    responsavel: '',
    prazo: '',
    status: 'pendente',
    prioridade: 'media',
    observacoes: '',
    data_inicio: new Date().toISOString().split('T')[0]
  });

  // Ações padrão sugeridas pela NR-05
  const acoesPadrao = [
    {
      descricao: "Elaboração e atualização do Mapa de Riscos",
      responsavel: "CIPA + SESMT",
      prazo: "30 dias",
      prioridade: "alta" as const,
      observacoes: "Identificar e mapear todos os riscos do ambiente de trabalho"
    },
    {
      descricao: "Campanha de Prevenção de Acidentes",
      responsavel: "CIPA",
      prazo: "60 dias",
      prioridade: "alta" as const,
      observacoes: "Desenvolver material educativo e realizar palestras"
    },
    {
      descricao: "Inspeção de Segurança nos Setores",
      responsavel: "Membros da CIPA",
      prazo: "45 dias",
      prioridade: "media" as const,
      observacoes: "Verificar condições de trabalho e uso de EPIs"
    },
    {
      descricao: "Treinamento de Segurança para Funcionários",
      responsavel: "CIPA + RH",
      prazo: "90 dias",
      prioridade: "alta" as const,
      observacoes: "Capacitar funcionários sobre procedimentos de segurança"
    },
    {
      descricao: "Análise de Acidentes e Incidentes",
      responsavel: "CIPA + SESMT",
      prazo: "15 dias",
      prioridade: "critica" as const,
      observacoes: "Investigar causas e propor medidas preventivas"
    },
    {
      descricao: "Verificação de Equipamentos de Proteção",
      responsavel: "CIPA + Manutenção",
      prazo: "30 dias",
      prioridade: "media" as const,
      observacoes: "Verificar estado e disponibilidade de EPIs"
    },
    {
      descricao: "Reunião Ordinária Mensal",
      responsavel: "CIPA",
      prazo: "Mensal",
      prioridade: "alta" as const,
      observacoes: "Avaliar andamento do plano e discutir melhorias"
    },
    {
      descricao: "Organização da SIPAT",
      responsavel: "CIPA + SESMT",
      prazo: "120 dias",
      prioridade: "alta" as const,
      observacoes: "Planejar e executar a Semana Interna de Prevenção"
    }
  ];

  useEffect(() => {
    carregarPlanoTrabalho();
  }, [empresaSelecionada]);

  const carregarPlanoTrabalho = async () => {
    console.log('🔍 Debug - empresaSelecionada:', empresaSelecionada);
    console.log('🔍 Debug - user:', user);
    console.log('🔍 Debug - empresaSelecionada.id:', empresaSelecionada?.id, 'tipo:', typeof empresaSelecionada?.id);
    console.log('🔍 Debug - user.id:', user?.id, 'tipo:', typeof user?.id);
    
    if (!empresaSelecionada?.id || !user?.id) {
      console.log('❌ Empresa ou usuário não selecionado');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      console.log('Carregando plano de trabalho do banco de dados...');
      
      // IDs são UUIDs (strings), não números
      const empresaId = empresaSelecionada.id;
      const usuarioId = user.id;
      
      console.log('🔍 IDs convertidos - empresaId:', empresaId, 'usuarioId:', usuarioId);
      
      // Tentar carregar plano existente
      let planoCompleto = await planoTrabalhoService.obterPlanoCompleto(empresaId);
      
      if (!planoCompleto) {
        // Se não existe, criar um plano padrão
        console.log('Criando plano padrão...');
        const planoId = await planoTrabalhoService.criarPlanoPadrao(
          empresaId,
          usuarioId,
          'Plano de Trabalho CIPA'
        );
        
        // Carregar o plano criado
        planoCompleto = await planoTrabalhoService.obterPlanoCompleto(empresaId);
      }
      
      setPlanoTrabalho(planoCompleto);
      console.log('Plano carregado com sucesso:', planoCompleto);
    } catch (error) {
      console.error('Erro ao carregar plano de trabalho:', error);
      alert('Erro ao carregar plano de trabalho. Verifique se as tabelas foram criadas no banco de dados.');
    } finally {
      setLoading(false);
    }
  };

  const adicionarAcaoPadrao = async (acao: typeof acoesPadrao[0]) => {
    if (!planoTrabalho?.plano?.id) return;

    try {
      const novaAcao = await planoTrabalhoService.salvarAcao({
        id_plano_trabalho: planoTrabalho.plano.id,
        descricao: acao.descricao,
        responsavel: acao.responsavel,
        prazo: acao.prazo,
        status: 'pendente',
        prioridade: acao.prioridade === 'critica' ? 'urgente' : acao.prioridade,
        observacoes: acao.observacoes,
        data_inicio: new Date().toISOString().split('T')[0]
      });

      // Atualizar estado local
      setPlanoTrabalho(prev => prev ? {
        ...prev,
        acoes: [...prev.acoes, novaAcao]
      } : null);

      // Registrar no histórico
      await planoTrabalhoService.registrarHistorico(
        planoTrabalho.plano.id,
        user?.id || '',
        'acao_adicionada',
        `Ação adicionada: ${acao.descricao}`
      );

      console.log('Ação adicionada e salva no banco:', acao.descricao);
    } catch (error) {
      console.error('Erro ao adicionar ação:', error);
      alert('Erro ao adicionar ação. Tente novamente.');
    }
  };

  const adicionarMeta = async () => {
    if (novaMeta.trim() && planoTrabalho?.plano?.id) {
      try {
        const novaMetaDB = await planoTrabalhoService.salvarMeta({
          id_plano_trabalho: planoTrabalho.plano.id,
          meta: novaMeta.trim(),
          ordem: planoTrabalho.metas.length
        });

        // Atualizar estado local
        setPlanoTrabalho(prev => prev ? {
          ...prev,
          metas: [...prev.metas, novaMetaDB]
        } : null);

        setNovaMeta('');
        setMostrarInputMeta(false);

        // Registrar no histórico
        await planoTrabalhoService.registrarHistorico(
          planoTrabalho.plano.id,
          user?.id || '',
          'meta_adicionada',
          `Meta adicionada: ${novaMeta.trim()}`
        );

        console.log('Meta adicionada e salva no banco:', novaMeta.trim());
      } catch (error) {
        console.error('Erro ao adicionar meta:', error);
        alert('Erro ao adicionar meta. Tente novamente.');
      }
    }
  };

  const removerMeta = async (metaId: string) => {
    if (!planoTrabalho?.plano?.id) return;

    try {
      await planoTrabalhoService.removerMeta(metaId);

      // Atualizar estado local
      setPlanoTrabalho(prev => prev ? {
        ...prev,
        metas: prev.metas.filter(meta => meta.id !== metaId)
      } : null);

      // Registrar no histórico
      await planoTrabalhoService.registrarHistorico(
        planoTrabalho.plano.id,
        user?.id || '',
        'meta_removida',
        `Meta removida`
      );

      console.log('Meta removida do banco:', metaId);
    } catch (error) {
      console.error('Erro ao remover meta:', error);
      alert('Erro ao remover meta. Tente novamente.');
    }
  };

  const salvarAcao = async () => {
    if (!planoTrabalho?.plano?.id || !novaAcao.descricao) return;

    try {
      const acaoData = {
        id: acaoEditando?.id,
        id_plano_trabalho: planoTrabalho.plano.id,
        descricao: novaAcao.descricao,
        responsavel: novaAcao.responsavel || '',
        prazo: novaAcao.prazo || '',
        status: novaAcao.status || 'pendente',
        prioridade: novaAcao.prioridade || 'media',
        observacoes: novaAcao.observacoes || '',
        data_inicio: novaAcao.data_inicio || new Date().toISOString().split('T')[0],
        data_conclusao: acaoEditando?.data_conclusao
      };

      const acaoSalva = await planoTrabalhoService.salvarAcao(acaoData);

      if (acaoEditando) {
        // Editar ação existente
        setPlanoTrabalho(prev => prev ? {
          ...prev,
          acoes: prev.acoes.map(a => a.id === acaoSalva.id ? acaoSalva : a)
        } : null);
      } else {
        // Adicionar nova ação
        setPlanoTrabalho(prev => prev ? {
          ...prev,
          acoes: [...prev.acoes, acaoSalva]
        } : null);
      }

      // Registrar no histórico
      await planoTrabalhoService.registrarHistorico(
        planoTrabalho.plano.id,
        user?.id || '',
        acaoEditando ? 'acao_editada' : 'acao_criada',
        `${acaoEditando ? 'Ação editada' : 'Ação criada'}: ${novaAcao.descricao}`
      );

      setMostrarModalAcao(false);
      setAcaoEditando(null);
      setNovaAcao({
        descricao: '',
        responsavel: '',
        prazo: '',
        status: 'pendente',
        prioridade: 'media',
        observacoes: '',
        data_inicio: new Date().toISOString().split('T')[0]
      });

      console.log('Ação salva no banco:', novaAcao.descricao);
    } catch (error) {
      console.error('Erro ao salvar ação:', error);
      alert('Erro ao salvar ação. Tente novamente.');
    }
  };

  const excluirAcao = async (id: string) => {
    if (!planoTrabalho?.plano?.id) return;
    
    try {
      await planoTrabalhoService.removerAcao(id);

      // Atualizar estado local
      setPlanoTrabalho(prev => prev ? {
        ...prev,
        acoes: prev.acoes.filter(acao => acao.id !== id)
      } : null);

      // Registrar no histórico
      await planoTrabalhoService.registrarHistorico(
        planoTrabalho.plano.id,
        user?.id || '',
        'acao_removida',
        'Ação removida'
      );

      console.log('Ação excluída do banco:', id);
    } catch (error) {
      console.error('Erro ao excluir ação:', error);
      alert('Erro ao excluir ação. Tente novamente.');
    }
  };

  const editarAcao = (acao: AcaoPlanoTrabalho) => {
    setAcaoEditando(acao);
    setNovaAcao({
      descricao: acao.descricao,
      responsavel: acao.responsavel,
      prazo: acao.prazo,
      status: acao.status,
      prioridade: acao.prioridade,
      observacoes: acao.observacoes,
      data_inicio: acao.data_inicio
    });
    setMostrarModalAcao(true);
  };

  const alterarStatusAcao = async (id: string, novoStatus: AcaoPlanoTrabalho['status']) => {
    if (!planoTrabalho?.plano?.id) return;

    try {
      const acaoAtualizada = await planoTrabalhoService.salvarAcao({
        id: id,
        status: novoStatus,
        data_conclusao: novoStatus === 'concluida' ? new Date().toISOString().split('T')[0] : undefined
      });

      // Atualizar estado local
      setPlanoTrabalho(prev => prev ? {
        ...prev,
        acoes: prev.acoes.map(acao => 
          acao.id === id ? acaoAtualizada : acao
        )
      } : null);

      // Registrar no histórico
      await planoTrabalhoService.registrarHistorico(
        planoTrabalho.plano.id,
        user?.id || '',
        'status_alterado',
        `Status da ação alterado para: ${novoStatus}`
      );

      console.log('Status da ação alterado no banco:', id, novoStatus);
    } catch (error) {
      console.error('Erro ao alterar status da ação:', error);
      alert('Erro ao alterar status da ação. Tente novamente.');
    }
  };

  const getStatusColor = (status: AcaoPlanoTrabalho['status']) => {
    switch (status) {
      case 'pendente': return 'bg-gray-100 text-gray-800';
      case 'em_andamento': return 'bg-blue-100 text-blue-800';
      case 'concluida': return 'bg-green-100 text-green-800';
      case 'cancelada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPrioridadeColor = (prioridade: AcaoPlanoTrabalho['prioridade']) => {
    switch (prioridade) {
      case 'baixa': return 'bg-green-100 text-green-800';
      case 'media': return 'bg-yellow-100 text-yellow-800';
      case 'alta': return 'bg-orange-100 text-orange-800';
      case 'urgente': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const exportarPlano = () => {
    if (!planoTrabalho) return;

    const conteudo = `
PLANO DE TRABALHO DA CIPA - ${empresaSelecionada?.nome || 'Empresa'}
Ano: ${new Date().getFullYear()}
Data de Elaboração: ${planoTrabalho.plano?.created_at ? new Date(planoTrabalho.plano.created_at).toLocaleDateString('pt-BR') : 'N/A'}
Status: ${planoTrabalho.plano?.status || 'Ativo'}

METAS:
${planoTrabalho.metas?.map(meta => `• ${meta.meta}`).join('\n') || 'Nenhuma meta definida'}

AÇÕES PLANEJADAS:
${planoTrabalho.acoes?.map(acao => `
${acao.descricao}
Responsável: ${acao.responsavel}
Prazo: ${acao.prazo}
Status: ${acao.status}
Prioridade: ${acao.prioridade}
Observações: ${acao.observacoes}
`).join('\n')}

Observações Gerais:
${planoTrabalho.plano?.observacoes_gerais || 'Nenhuma observação'}
    `;

    const blob = new Blob([conteudo], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plano-trabalho-cipa-${new Date().getFullYear()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Activity className="w-8 h-8 text-blue-600 animate-pulse mx-auto mb-2" />
          <p className="text-gray-600">Carregando plano de trabalho...</p>
        </div>
      </div>
    );
  }

  if (!planoTrabalho) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 text-red-600 mx-auto mb-2" />
          <p className="text-gray-600">Erro ao carregar plano de trabalho</p>
          <button 
            onClick={carregarPlanoTrabalho}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Plano de Trabalho da CIPA</h1>
            <p className="text-blue-100">
              {empresaSelecionada?.nome} - Ano {new Date().getFullYear()}
            </p>
            <p className="text-sm text-blue-200 mt-1">
              Conforme NR-05 - Art. 5.16 (e) e (f)
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportarPlano}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Download className="w-4 h-4" />
              Exportar
            </button>
          </div>
        </div>
      </div>

      {/* Informações do Plano */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-blue-500">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">Data de Elaboração</span>
          </div>
          <p className="text-lg font-bold text-gray-900">
            {planoTrabalho?.plano?.created_at ? 
              new Date(planoTrabalho.plano.created_at).toLocaleDateString('pt-BR') : 
              'Não definida'
            }
          </p>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-green-500">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-gray-700">Aprovado por</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{planoTrabalho?.plano?.status || 'Ativo'}</p>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-purple-500">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">Total de Ações</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{planoTrabalho?.acoes?.length || 0}</p>
        </div>
      </div>

      {/* Metas */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            Metas do Plano de Trabalho
          </h3>
          <button
            onClick={() => setMostrarInputMeta(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Nova Meta
          </button>
        </div>
        
        {/* Input para nova meta */}
        {mostrarInputMeta && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={novaMeta}
                onChange={(e) => setNovaMeta(e.target.value)}
                placeholder="Digite uma nova meta..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && adicionarMeta()}
                autoFocus
              />
              <button
                onClick={adicionarMeta}
                disabled={!novaMeta.trim()}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setNovaMeta('');
                  setMostrarInputMeta(false);
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        
        <div className="space-y-2">
          {planoTrabalho?.metas?.map((meta) => (
            <div key={meta.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-gray-800 flex-1">{meta.meta}</span>
              <button
                onClick={() => removerMeta(meta.id)}
                className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                title="Remover meta"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Botão de Sugestões */}
      <div className="flex justify-center mb-4">
        <button
          onClick={() => setMostrarSugestoes(!mostrarSugestoes)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-lg"
        >
          <Plus className="w-5 h-5" />
          {mostrarSugestoes ? 'Ocultar Sugestões' : 'Ver Sugestões de Ações (NR-05)'}
        </button>
      </div>

      {/* Ações Padrão Sugeridas */}
      {mostrarSugestoes && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-green-600" />
            Ações Padrão Sugeridas (NR-05)
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Clique em uma ação para adicioná-la ao seu plano de trabalho:
          </p>
          
          {(() => {
            const sugestoesDisponiveis = acoesPadrao.filter(acao => {
              return !planoTrabalho?.acoes?.some(acaoPlano => 
                acaoPlano.descricao.toLowerCase().trim() === acao.descricao.toLowerCase().trim()
              );
            });

            if (sugestoesDisponiveis.length === 0) {
              return (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">Todas as sugestões foram adicionadas!</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Você já adicionou todas as ações padrão sugeridas ao seu plano de trabalho.
                  </p>
                </div>
              );
            }

            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {sugestoesDisponiveis.map((acao, index) => (
                  <button
                    key={index}
                    onClick={() => adicionarAcaoPadrao(acao)}
                    className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-sm">{acao.descricao}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPrioridadeColor(acao.prioridade === 'critica' ? 'urgente' : acao.prioridade)}`}>
                        {acao.prioridade === 'critica' ? 'urgente' : acao.prioridade}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">
                      <strong>Responsável:</strong> {acao.responsavel}
                    </p>
                    <p className="text-xs text-gray-600 mb-1">
                      <strong>Prazo:</strong> {acao.prazo}
                    </p>
                    <p className="text-xs text-gray-500">{acao.observacoes}</p>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Ações do Plano */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Ações do Plano de Trabalho
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => setEditando(!editando)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                editando 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
              }`}
            >
              <Edit className="w-4 h-4" />
              {editando ? 'Sair da Edição' : 'Editar'}
            </button>
            {editando && (
              <button
                onClick={() => setMostrarModalAcao(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nova Ação
              </button>
            )}
          </div>
        </div>

        {planoTrabalho?.acoes?.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Nenhuma ação cadastrada ainda.</p>
            {editando && (
              <p className="text-sm text-gray-500">
                Use as ações padrão sugeridas acima ou crie uma nova ação personalizada.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {planoTrabalho?.acoes?.map((acao) => (
              <div key={acao.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-2">{acao.descricao}</h4>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(acao.status)}`}>
                        {acao.status.replace('_', ' ')}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPrioridadeColor(acao.prioridade)}`}>
                        {acao.prioridade}
                      </span>
                    </div>
                  </div>
                  {editando && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => editarAcao(acao)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => excluirAcao(acao.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Responsável:</span>
                    <p className="text-gray-600">{acao.responsavel}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Prazo:</span>
                    <p className="text-gray-600">{acao.prazo}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Data Início:</span>
                    <p className="text-gray-600">
                      {new Date(acao.data_inicio || '').toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
                
                {acao.observacoes && (
                  <div className="mt-3">
                    <span className="font-medium text-gray-700 text-sm">Observações:</span>
                    <p className="text-gray-600 text-sm mt-1">{acao.observacoes}</p>
                  </div>
                )}

                {editando && (
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => alterarStatusAcao(acao.id, 'pendente')}
                      className={`px-3 py-1 rounded text-xs ${
                        acao.status === 'pendente' 
                          ? 'bg-gray-200 text-gray-800' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Pendente
                    </button>
                    <button
                      onClick={() => alterarStatusAcao(acao.id, 'em_andamento')}
                      className={`px-3 py-1 rounded text-xs ${
                        acao.status === 'em_andamento' 
                          ? 'bg-blue-200 text-blue-800' 
                          : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      }`}
                    >
                      Em Andamento
                    </button>
                    <button
                      onClick={() => alterarStatusAcao(acao.id, 'concluida')}
                      className={`px-3 py-1 rounded text-xs ${
                        acao.status === 'concluida' 
                          ? 'bg-green-200 text-green-800' 
                          : 'bg-green-100 text-green-600 hover:bg-green-200'
                      }`}
                    >
                      Concluído
                    </button>
                    <button
                      onClick={() => alterarStatusAcao(acao.id, 'cancelada')}
                      className={`px-3 py-1 rounded text-xs ${
                        acao.status === 'cancelada' 
                          ? 'bg-red-200 text-red-800' 
                          : 'bg-red-100 text-red-600 hover:bg-red-200'
                      }`}
                    >
                      Atrasado
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Observações Gerais */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          Observações Gerais
        </h3>
        <p className="text-gray-700 mb-4">{planoTrabalho?.plano?.observacoes_gerais}</p>
        
        {/* Botão de Exportar */}
        <div className="flex justify-center pt-4 border-t border-gray-200">
          <button
            onClick={exportarPlano}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-lg"
          >
            <Download className="w-5 h-5" />
            Exportar Plano de Trabalho
          </button>
        </div>
      </div>

      {/* Modal para Nova/Editar Ação */}
      {mostrarModalAcao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {acaoEditando ? 'Editar Ação' : 'Nova Ação'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição da Ação *
                </label>
                <textarea
                  value={novaAcao.descricao || ''}
                  onChange={(e) => setNovaAcao({...novaAcao, descricao: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Descreva a ação a ser realizada..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Responsável
                  </label>
                  <input
                    type="text"
                    value={novaAcao.responsavel || ''}
                    onChange={(e) => setNovaAcao({...novaAcao, responsavel: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nome do responsável"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prazo
                  </label>
                  <input
                    type="text"
                    value={novaAcao.prazo || ''}
                    onChange={(e) => setNovaAcao({...novaAcao, prazo: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 30 dias, 15/12/2024"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={novaAcao.status || 'pendente'}
                    onChange={(e) => setNovaAcao({...novaAcao, status: e.target.value as AcaoPlanoTrabalho['status']})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="pendente">Pendente</option>
                    <option value="em_andamento">Em Andamento</option>
                    <option value="concluido">Concluído</option>
                    <option value="atrasado">Atrasado</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prioridade
                  </label>
                  <select
                    value={novaAcao.prioridade || 'media'}
                    onChange={(e) => setNovaAcao({...novaAcao, prioridade: e.target.value as AcaoPlanoTrabalho['prioridade']})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="baixa">Baixa</option>
                    <option value="media">Média</option>
                    <option value="alta">Alta</option>
                    <option value="critica">Crítica</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Início
                </label>
                <input
                  type="date"
                  value={novaAcao.data_inicio || ''}
                  onChange={(e) => setNovaAcao({...novaAcao, data_inicio: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={novaAcao.observacoes || ''}
                  onChange={(e) => setNovaAcao({...novaAcao, observacoes: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                  placeholder="Observações adicionais..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setMostrarModalAcao(false);
                  setAcaoEditando(null);
                  setNovaAcao({
                    descricao: '',
                    responsavel: '',
                    prazo: '',
                    status: 'pendente',
                    prioridade: 'media',
                    observacoes: '',
                    data_inicio: new Date().toISOString().split('T')[0]
                  });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={salvarAcao}
                disabled={!novaAcao.descricao}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {acaoEditando ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanoTrabalhoTab;
